
import Vue from 'vue'
import { Component } from 'vue-property-decorator'
import * as Sentry from '@sentry/vue'
import axios from 'axios'

import ConfirmModal from '@/domains/global/components/ConfirmModal.vue'
import PageHeader from '@/domains/global/components/PageHeader.vue'
import { TranslationActivity, TranslationMarkup } from '@/domains/global/models'
import ActivityUploadDocument from '../components/ActivityDocumentUpload/ActivityUploadDocument.vue'
import TranslateTable from '../components/TranslateTable/TranslateTable.vue'
import FieldSelect from '@/domains/activities/components/FieldSelect/FieldSelect.vue'

type MarkupData = {
  id?: string;
  translatedValue: string | null;
  rawValue: string | null;
}

@Component({
  components: { FieldSelect, PageHeader, ActivityUploadDocument, TranslateTable }
})
export default class FinaliseActivityPage extends Vue {
  activity: Partial<TranslationActivity> = {}
  fields: TranslationMarkup[] = []
  sourceImages: any[] = []
  isLoading = true

  get cancelModal () {
    return this.$refs.cancelModal as ConfirmModal<TranslationActivity>
  }

  get confirmSkipModal() {
    return this.$refs.confirmSkipModal as ConfirmModal<TranslationActivity>
  }

  get confirmSkipFieldsModal() {
    return this.$refs.confirmSkipFieldsModal as ConfirmModal<TranslationActivity>
  }

  get allFieldsCompleted() {
    return true
  }

  get allFieldsTranslated() {
    return true
  }

  get croppedFieldCount() {
    const fields = this.fields || []
    return fields.filter(field => !!field.completed).length
  }

  get fieldCount() {
    const fields = this.fields || []
    return fields.length
  }

  get translatedFieldCount() {
    const fields = this.fields || []
    return fields.filter(field => !!field.translatedValue?.length).length
  }

  get translationTitle() {
    if (!this.activity) {
      return ''
    }

    if (this.activity.documentType?.toLowerCase() === 'adhoc') {
      return 'Full Translation'
    }

    return this.activity.templateTypeName || ''
  }

  get jobId() {
    return this.$route.params.jobId
  }

  get translationId() {
    return this.$route.params.id
  }

  get stepNumber() {
    switch (this.step) {
      case 'translate':
        return 3
      case 'select':
        return 2
      default:
        return 1
    }
  }

  set stepNumber(value) {
    switch (+value) {
      case 3:
        this.$router.push({ query: { step: 'translate' } })
        break
      case 2:
        this.$router.push({ query: { step: 'select' } })
        break
      case 1:
      default:
        this.$router.push({ query: { step: 'upload' } })
        break
    }
  }

  get step() {
    return this.$route.query.step
  }

  get isStepperRequired() {
    return !!this.activity.id && this.activity.documentType === 'customer-template'
  }

  get isNextStepEnabled() {
    if (this.isStepperRequired) {
      if (this.stepNumber > 1) {
        return true
      }
    }
    return !!this.activity.translationSources && !!this.activity.translationSources.length && this.activity.translationSources.every(source => source.status === 'processed')
  }

  get nextStepText() {
    if (this.$vuetify.breakpoint.xsOnly && this.isStepperRequired) {
      if (this.step === 'translate') {
        return 'Next Step: Summary'
      }

      if (this.step === 'select') {
        return 'Next Step: Translate'
      }

      if (this.step === 'upload') {
        return 'Next Step: Select Fields'
      }
    }

    return 'Continue'
  }

  async mounted() {
    this.isLoading = true
    window.onbeforeunload = this.beforeUnloadListener
    if (!this.step || !['upload', 'select', 'translate'].includes(this.$route.query.step as string)) {
      this.$router.replace({ query: { step: 'upload' } })
    }
    try {
      await this.getActivity()
    } finally {
      this.isLoading = false
    }
  }

  beforeDestroy() {
    window.onbeforeunload = null
  }

  beforeUnloadListener(event: Event) {
    event.preventDefault()
    return 'Are you sure you wish to leave this page? Any unsaved changes will be lost.'
  }

  async getActivity() {
    const response = await axios.get<TranslationActivity>(`/jobs/${this.jobId}/activities/${this.translationId}`)
    this.activity = response.data
    const processedSources = this.activity.translationSources?.filter(i => i.status === 'processed') || []
    this.sourceImages = processedSources.reduce((array, source) => {
      return [...array, ...source.images]
    }, [] as any[])

    if (!this.activity.translationSources?.length && this.step !== 'upload') {
      this.$router.replace({ query: { step: 'upload' } })
    }
  }

  async getMarkups() {
    const response = await axios.get<TranslationMarkup[]>(`/jobs/${this.jobId}/activities/${this.translationId}/markups`)
    const markups = response.data
    const currentFieldTranslations = this.fields.reduce((translationMap, field) => {
      const { id, translatedValue, rawValue } = field
      translationMap[id as any] = { translatedValue, rawValue }
      return translationMap
    }, {} as Record<number, MarkupData>)

    // To avoid losing translation data on a refetch, we override the data provided from the API
    this.fields = markups.map((markup) => {
      return {
        ...markup,
        ...currentFieldTranslations[markup.id as any],
        completed: !!(markup && markup.path)
      }
    })
  }

  onBackClick() {
    if (this.stepNumber > 1) {
      this.stepNumber--
    }
  }

  onContinueClick() {
    if (!this.isStepperRequired || this.stepNumber === 3) {
      if (this.stepNumber === 3 && !this.allFieldsTranslated) {
        this.confirmSkip()
        return
      }

      this.completePage(this.stepNumber === 3)

      return
    }

    if (this.stepNumber === 2 && !this.allFieldsCompleted) {
      this.confirmSkipFields()
      return
    }

    this.stepNumber++
  }

  completeField(id: string) {
    const fieldToUpdate = this.fields.find(field => field.id === id)
    if (fieldToUpdate) {
      fieldToUpdate.completed = true
      this.getMarkups()
    }
  }

  updateFieldTranslation(data: MarkupData) {
    const field = this.fields.find(field => field.id === data.id)
    if (field) {
      field.translatedValue = data.translatedValue
      field.rawValue = data.rawValue
    }
  }

  async completePage(saveMarkups = false) {
    try {
      if (saveMarkups) {
        await this.saveMarkups()
      }
      await this.submitActivity()
      this.$store.dispatch('success', 'Translation saved successfully')
      this.$router.push(`/jobs/${this.jobId}`)
    } catch (err) {
      Sentry.captureException(err)
      this.$store.dispatch('error', 'An error occurred submitting your activity')
    }
  }

  async submitActivity() {
    try {
      await axios.put(`/jobs/${this.jobId}/activities/${this.translationId}/submit`)
    } catch (error) {
      Sentry.captureException(error)
      this.$store.dispatch('error', 'An error occurred updating your activity')
    }
  }

  async saveMarkups() {}

  async resetField(field: TranslationMarkup) {}

  async saveForLater() {
    try {
      this.$store.dispatch('success', 'Changes saved successfully')
      this.$router.push(`/jobs/${this.jobId}`)
    } catch (err) {
      Sentry.captureException(err)
      this.$store.dispatch('error', 'An error occurred submitting your activity')
    }
  }

  confirmCancel() {
    this.cancelModal.open(this.activity as TranslationActivity)
  }

  confirmSkip() {
    this.confirmSkipModal.open(this.activity as TranslationActivity)
  }

  confirmSkipFields() {
    this.confirmSkipFieldsModal.open(this.activity as TranslationActivity)
  }
}
